// TestSeparatorAnalysis.js

import "./TestSeparatorAnalysis.css";
import { useContext, useEffect, useRef, useState } from "react";
import { WellContext } from "../context/WellContext";
import * as XLSX from "xlsx";
import html2canvas from "html2canvas";

function TestSeparatorAnalysis({ open, onClose }) {

  const { wellData } = useContext(WellContext);

  /* =====================================================
     DRAGGING SYSTEM
  ===================================================== */
  const [position, setPosition] = useState({
    x: 220,
    y: 80
  });

  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });

  const startDrag = (e) => {
    dragging.current = true;

    offset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };

  useEffect(() => {

    const move = (e) => {

      if (!dragging.current) return;

      setPosition({
        x: e.clientX - offset.current.x,
        y: e.clientY - offset.current.y
      });
    };

    const stop = () => {
      dragging.current = false;
    };

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", stop);

    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", stop);
    };

  }, [position]);

  /* =====================================================
     ACTIVE ANALYSIS MODE
  ===================================================== */
  const [mode, setMode] = useState("production");

  /* =====================================================
     LIVE FLOW VALUES
  ===================================================== */

  const [flows, setFlows] = useState({
    NK1: 0,
    NK2: 0,
    NK3: 0
  });

  useEffect(() => {

    const interval = setInterval(() => {

      setFlows({
        NK1: Number(localStorage.getItem("fic10104")) || 0,
        NK2: Number(localStorage.getItem("fic10204")) || 0,
        NK3: Number(localStorage.getItem("fic10304")) || 0
      });

    }, 1000);

    return () => clearInterval(interval);

  }, []);

  /* =====================================================
     CONVERSIONS
  ===================================================== */

  const perDay = (v) => v * 1440;
  const perYear = (v) => v * 1440 * 365;

  /* =====================================================
     WELL COMPONENT HELPERS
  ===================================================== */

  const getPercent = (well, component) => {

    return (
      Number(
        wellData?.[well]?.percentages?.[component]
      ) || 0
    );
  };

  const validFlow = (v) => v >= 1 ? v : 0;

  /* =====================================================
     TOTAL COMPONENT CALCULATOR
  ===================================================== */

  const calcComponent = (component) => {

    const nk1 =
      validFlow(flows.NK1) *
      (getPercent("NK1", component) / 100);

    const nk2 =
      validFlow(flows.NK2) *
      (getPercent("NK2", component) / 100);

    const nk3 =
      validFlow(flows.NK3) *
      (getPercent("NK3", component) / 100);

    return nk1 + nk2 + nk3;
  };

  /* =====================================================
     INDUSTRY GROUPINGS
  ===================================================== */

  const crude =
    calcComponent("C5+ liquid hydrocarbons (oil)") +
    calcComponent("Pentanes+ (C5+)") +
    calcComponent("Wax / paraffins") +
    calcComponent("Asphaltenes");

  const gas =
    calcComponent("Dissolved light hydrocarbons C1–C4") +
    calcComponent("Methane (CH4)") +
    calcComponent("Ethane (C2H6)") +
    calcComponent("Propane (C3H8)") +
    calcComponent("Butanes (i-C4 / n-C4)");

  const water =
    calcComponent("Formation water (H2O)");

  const h2s =
    calcComponent("Hydrogen sulfide (H2S)");

  const co2 =
    calcComponent("Carbon dioxide (CO2)");

  const sand =
    calcComponent("Sand");

  const salts =
    calcComponent("Dissolved salts");

  const wax =
    calcComponent("Wax / paraffins");

  const asphaltenes =
    calcComponent("Asphaltenes");

  const organicAcids =
    calcComponent("Organic acids");

  /* =====================================================
     ENGINEERING CALCULATIONS
  ===================================================== */

  const GOR = crude > 0
    ? gas / crude
    : 0;

  const waterCut =
    crude + water > 0
      ? (water / (crude + water)) * 100
      : 0;

  /* =====================================================
     TABLE GENERATOR
  ===================================================== */

  const makeRow = (name, value, unit) => ({
    name,
    pm: `${value.toFixed(2)} ${unit}`,
    pd: `${perDay(value).toFixed(2)} ${unit}`,
    py: `${perYear(value).toFixed(2)} ${unit}`
  });

  /* =====================================================
     PRODUCTION ANALYSIS
  ===================================================== */

  const productionRows = [
    makeRow("Total Crude", crude, "BBL"),
    makeRow("Total Gas", gas, "SCF"),
    makeRow("Formation Water", water, "L"),
    makeRow("Water Cut", waterCut, "%"),
  ];

  /* =====================================================
     CORROSION ANALYSIS
  ===================================================== */

  const corrosionRows = [
    makeRow("Hydrogen Sulphide (H2S)", h2s, "ppm"),
    makeRow("Carbon Dioxide (CO2)", co2, "mol%"),
    makeRow("Dissolved Salts", salts, "ppm"),
    makeRow("Organic Acids", organicAcids, "mg/L"),
    makeRow("Sand", sand, "mg/L"),
  ];

  /* =====================================================
     FLOW ASSURANCE ANALYSIS
  ===================================================== */

  const flowRows = [
    makeRow("Wax / Paraffins", wax, "wt%"),
    makeRow("Asphaltenes", asphaltenes, "wt%"),
  ];

  /* =====================================================
     ACTIVE TABLE
  ===================================================== */

  let rows = [];

  if (mode === "production") {
    rows = productionRows;
  }

  if (mode === "corrosion") {
    rows = corrosionRows;
  }

  if (mode === "flow") {
    rows = flowRows;
  }

  /* =====================================================
     EXPORT EXCEL
  ===================================================== */

  const exportExcel = () => {

    const sheet = [
      ["Parameter", "PM", "PD", "PY"],

      ...rows.map(r => [
        r.name,
        r.pm,
        r.pd,
        r.py
      ])
    ];

    const ws = XLSX.utils.aoa_to_sheet(sheet);

    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      "Test Separator Analysis"
    );

    XLSX.writeFile(
      wb,
      "Test_Separator_Analysis.xlsx"
    );
  };

  /* =====================================================
     SHARE
  ===================================================== */

  const [sharing, setSharing] = useState(false);

  const shareWindow = async () => {

    try {

      if (sharing) return;

      setSharing(true);

      const el = document.querySelector(".tsa-window");

      if (!el) return;

      const canvas = await html2canvas(el, {
        scale: 1.5,
        useCORS: true,
        backgroundColor: "#0f172a"
      });

      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, "image/png");
      });

      if (!blob) return;

      const file = new File(
        [blob],
        "test-analysis.png",
        {
          type: "image/png"
        }
      );

      if (
        navigator.share &&
        navigator.canShare?.({ files: [file] })
      ) {

        await navigator.share({
          files: [file],
          title: "Test Separator Analysis"
        });

      }

    } catch (err) {

      console.log(err);

    } finally {

      setSharing(false);

    }
  };

  /* =====================================================
     PRINT
  ===================================================== */

  const printWindow = () => {
    window.print();
  };

  /* =====================================================
     RESET
  ===================================================== */

  const resetValues = () => {

    localStorage.setItem("fic10104", "0");
    localStorage.setItem("fic10204", "0");
    localStorage.setItem("fic10304", "0");
  };

  /* =====================================================
     CLOSE
  ===================================================== */

  if (!open) return null;

  return (

    <div
      className="tsa-overlay"
      style={{
        pointerEvents: "none"
      }}
    >

      <div
        className="tsa-window"
        style={{
          left: position.x,
          top: position.y,
          pointerEvents: "auto"
        }}
      >

        {/* =====================================================
           HEADER
        ===================================================== */}

        <div
          className="tsa-header"
          onMouseDown={startDrag}
        >

          <div className="tsa-header-center">

            <div className="tsa-title">
              NK TECH INDUSTRIAL DCS AND DIGITAL TWIN SOLUTION
            </div>

            <div className="tsa-subtitle">
              TEST SEPARATOR FLUID ANALYSIS
            </div>

          </div>

          <button
            className="tsa-close"
            onClick={onClose}
          >
            ✕
          </button>

        </div>

        {/* =====================================================
           TOOLBAR
        ===================================================== */}

        <div className="tsa-toolbar">

          <button
            onClick={() => setMode("production")}
          >
            Production Phase Analysis
          </button>

          <button
            onClick={() => setMode("corrosion")}
          >
            Contaminant / Corrosion Analysis
          </button>

          <button
            onClick={() => setMode("flow")}
          >
            Flow Assurance Analysis
          </button>

        </div>

        {/* =====================================================
           BODY
        ===================================================== */}

        <div className="tsa-body">

          <div className="tsa-table-header">

            <span>PARAMETER</span>
            <span>PM</span>
            <span>PD</span>
            <span>PY</span>

          </div>

          {rows.map((row) => (

            <div
              className="tsa-row"
              key={row.name}
            >

              <span>{row.name}</span>
              <span>{row.pm}</span>
              <span>{row.pd}</span>
              <span>{row.py}</span>

            </div>

          ))}

          {/* =====================================================
             GOR
          ===================================================== */}

          <div className="tsa-gor">

            <strong>
              Gas Oil Ratio (GOR):
            </strong>

            {" "}
            {GOR.toFixed(2)}

          </div>

          {/* =====================================================
             DISCLAIMER
          ===================================================== */}

          <div className="tsa-disclaimer">

            <strong>Disclaimer:</strong>

            {" "}

            This analysis module is intended
            for operator training, digital
            twin visualization, process
            familiarization, and engineering
            simulation purposes only.

          </div>

        </div>

        {/* =====================================================
           FOOTER
        ===================================================== */}

        <div className="tsa-footer">

          <button onClick={resetValues}>
            Reset
          </button>

          <button
            onClick={shareWindow}
            disabled={sharing}
          >
            {sharing ? "Sharing..." : "Share"}
          </button>

          <button onClick={printWindow}>
            Print PDF
          </button>

          <button onClick={exportExcel}>
            Export Excel
          </button>

        </div>

      </div>

    </div>
  );
}

export default TestSeparatorAnalysis;